import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Context } from 'js/components/LocaleWrapper';
import { CmsFilters } from 'js/model/cms/cms-browse-page';
import { arrayWithoutNullables } from 'js/helpers/array';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { trackStructuredEvent } from 'js/helpers/google-tag-manager';
import { BrowsePageOutput } from 'js/model/rainbow/browse-page/BrowsePageOutput';
import { DeviceMobile, useDevice } from '@treatwell/ui';
import { ContextualFilterButton } from './ContextualFilterButton';
import styles from './ContextualFilter.module.css';
import { trackPopularTreatmentClicked } from '../tracking/trackSearch';

interface Props {
  aggregations: Aggregations;
  cmsFilters: CmsFilters;
  browsePageOutput: BrowsePageOutput;
}

export const ContextualFilter = ({
  aggregations,
  cmsFilters,
  browsePageOutput,
}: Props) => {
  const { t } = useTranslation();
  const { channel, pageData } = useContext(Context);
  const [filterApplied, setFilterdApplied] = useState<{
    position: number;
    id: number;
    isRelatedTreatment: boolean;
    treatmentTypeId?: number;
  } | null>();
  const { type: deviceType } = useDevice();
  const { relatedTreatments, specification } = browsePageOutput;
  const isRelatedTreatment = Boolean(
    pageData.showRelatedTreatments && relatedTreatments
  );

  useEffect(() => {
    if (filterApplied && !filterApplied.isRelatedTreatment) {
      trackPopularTreatmentClicked(
        channel.country.countryCode,
        deviceType === DeviceMobile,
        browsePageOutput.pagination.totalElements,
        filterApplied.id,
        filterApplied.position,
        filterApplied.treatmentTypeId
      );
      setFilterdApplied(null);
    }
  }, [
    browsePageOutput.pagination.totalElements,
    channel.country.countryCode,
    deviceType,
    filterApplied,
  ]);

  useEffect(() => {
    // Track whether or not the experiment is enabled
    trackStructuredEvent({
      property: 'related_treatment_experiment',
      category: 'browse',
      action: 'load',
      label: pageData.showRelatedTreatments
        ? 'related_treatment_experiment_enabled'
        : 'related_treatment_experiment_disabled',
    });
    // Record whether or not the search has available relatedTreatments.
    if (relatedTreatments && relatedTreatments.length > 0) {
      trackStructuredEvent({
        property: 'related_treatment_experiment',
        category: 'browse',
        action: 'show',
        label: 'has_related_treatment',
      });
    } else {
      trackStructuredEvent({
        property: 'related_treatment_experiment',
        category: 'browse',
        action: 'show',
        label: 'no_related_treatment',
      });
    }
  }, [pageData.showRelatedTreatments, relatedTreatments]);

  const treatments = useMemo(() => {
    if (
      pageData.showRelatedTreatments &&
      relatedTreatments &&
      relatedTreatments.length > 0
    ) {
      return relatedTreatments.map((relatedTreatment) => ({
        id: relatedTreatment.treatmentId.id,
        name: relatedTreatment.name,
      }));
    }
    if (
      specification.treatmentCategoryGroup &&
      !specification.treatmentCategories
    ) {
      const treatmentTypeId =
        specification.treatmentCategoryGroup.id.toString();
      if (!cmsFilters['popular-treatments'][treatmentTypeId]) {
        return [];
      }
      return arrayWithoutNullables(
        cmsFilters[`popular-treatments`][treatmentTypeId]
          .split(',')
          // find the treatment for each treatment id
          .map((id) =>
            aggregations.treatmentCategories.find(
              (treatment) => treatment.id === parseInt(id, 10)
            )
          )
      );
    }

    return [];
  }, [
    pageData.showRelatedTreatments,
    relatedTreatments,
    specification,
    aggregations,
    cmsFilters,
  ]);

  if (treatments.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.headline}>
        {isRelatedTreatment
          ? t('browsePage.filter.related')
          : cmsFilters['popular-treatments-heading']}
      </div>
      <div className={styles.filters} data-cy="ContextualFilter">
        <div className={styles.buttons}>
          {treatments.map((treatment, index) => (
            <ContextualFilterButton
              label={treatment.name}
              key={treatment.id}
              id={treatment.id}
              isRelatedTreatment={isRelatedTreatment}
              onFilterApplied={() => {
                setFilterdApplied({
                  id: treatment.id,
                  position: index + 1,
                  treatmentTypeId:
                    browsePageOutput.specification.treatmentCategoryGroup?.id,
                  isRelatedTreatment,
                });
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};
